import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import moment from 'moment';
import { type ErrorRecord } from 'src/hooks/useForm';
import EmailDistributionService from 'src/services/emailDistribution/emailDistributionService';
import IncidentService from 'src/services/incident/incidentService';
import { type AustinCase } from 'src/services/incident/types';
import JmrService from 'src/services/jmr/jmrService';
import PeopleApiService from 'src/services/peopleApiService';
import PlacementDetailsService, { type InitiatePlacementTransfer } from 'src/services/placementDetailsService';
import SitesService from 'src/services/sites/sitesService';
import {
  type AccommodationStatus,
  type AccommodationStatusWithDecisionByLoginNeeded,
  AgreeWithHealthCareRecommendationsEnum,
  type EditAssociateInfoModalFormData,
  type getPlacementIdByCaseIdAndLoginPayload,
  type InitiatePlacementTransferPayloadProps,
  type OomdForm,
  type PlacementDetails,
  type PlacementDetailsPageState,
  type PlacementDetailsRevision,
  ReferralAndClaimsStatusEnum,
  type SendEmailFormData,
  type UpdateEndDateForm,
} from 'src/store/placementDetailsPageSlice/types';
import { datePickerStringToDateObj } from 'src/utils/dateUtils';
import { createFileUrl, downloadFile } from 'src/utils/fileUtils';
import { isNumber } from 'src/utils/helpers';

import { type Site } from '../siteSlice';

// **** Functions **** //

/**
 * Format the placement details to UI specifications
 */
const formatPlacementDetailsPayload = (payload: PlacementDetails) => {
  const newRevisions = payload.revisions
    // Round revision date to nearest minute
    .map((revision: PlacementDetailsRevision) => ({
      ...revision,
      revisionDate: new Date(revision.revisionDate).getTime(),
    }))
    // Sort by revisionDate
    .sort((a: any, b: any) => new Date(a.revisionDate).getTime() - new Date(b.revisionDate).getTime());
  // Return
  return {
    ...payload,
    startAtDate: moment(payload.startAtDate).format('L'),
    endAtDate: moment(payload.endAtDate).format('L'),
    transferredAtDate: moment(payload.transferredAtDate).format('L'),
    revisions: newRevisions,
  };
};

/**
 * Create a map of timestamps and s3 keys.
 */
const createHashMapFromS3Keys = (keys: string[]) => {
  const map: any = {};
  for (const key of keys) {
    // String is of format jmr_{accommodation_id}_{login}_{timestamp}.pdf
    const timestampPortion = key.split('_')[3];
    const timestampPortionWithoutFileExtension = +timestampPortion.split('.')[0];
    map[timestampPortionWithoutFileExtension] = key;
  }
  return map;
};

/**
 * Get placement detail data.
 */
export const getPlacementDetails = createAsyncThunk(
  'placementDetails/getPlacementDetails',
  async (accommodationId: number) => {
    const { data } = await JmrService.getAccommodationDetail(accommodationId);
    return data;
  },
);

/**
 * Get emails for transfer-placment.
 */
export const getEmailsForTransferPlacement = createAsyncThunk(
  'placementDetails/getEmailsForTransfer',
  async (destinationSiteId: number, { getState }) => {
    const state = await (getState() as any);
    const { placementDetails } = state.placementDetailsPage;
    const { allowedSites } = state.sites;
    const sourceSiteId = allowedSites.filter((site: Site) => site.site === placementDetails.fc)[0].id;
    const sourceSiteEmails = await EmailDistributionService.getForSite(sourceSiteId);
    const destinationSiteEmails = await EmailDistributionService.getForSite(destinationSiteId);
    const sourceSiteApprovedEmails = sourceSiteEmails.data?.placementEmailRecipients || '';
    const destinationSiteApprovedEmails = destinationSiteEmails.data?.placementEmailRecipients || '';
    const includeManager =
      sourceSiteEmails.data?.supervisorsForPlacementEmail ||
      destinationSiteEmails.data?.supervisorsForPlacementEmail ||
      false;
    let emails = `${sourceSiteApprovedEmails};${destinationSiteApprovedEmails}`;
    emails = includeManager ? `${emails};${placementDetails.managerAlias}@amazon.com` : emails;
    // Get rid of trailing/leading semi colon and any empty emails
    emails = emails
      .split(';')
      .map((email) => email.trim())
      .filter((email) => email.length > 0)
      .join(';');
    return emails;
  },
);

/**
 * Placement-Id by External-Id and Redirect.
 */
export const getPlacementIdByExternalIdAndRedirect = createAsyncThunk(
  'placementDetails/getPlacementIdByCaseIdAndLogin',
  async (payload: getPlacementIdByCaseIdAndLoginPayload) => {
    const { type, caseId, login } = payload;
    const { data } = await JmrService.getAccommodationIdByCaseIdAndLogin(type, caseId, login);
    return data;
  },
);

/**
 * Download OOMD.
 */
export const downloadOOMD = createAsyncThunk('placementDetails/downloadOOMD', async (_, { getState }) => {
  const placementDetailsPageState = (await (getState() as any).placementDetailsPage) as PlacementDetailsPageState;
  const oomdFormData = placementDetailsPageState.oomdFormData;
  const { data } = await JmrService.downloadOOMD({
    accommodationId: placementDetailsPageState.placementId,
    injury: oomdFormData.injury || '',
    reportDate: oomdFormData.dateOfInjury || '',
    reportDateToAMCARE: oomdFormData.dateOfInjuryReportedToAmcare || '',
    additionalNotes: oomdFormData.additionalNotes || '',
  });
  return data;
});

/**
 * Delete placement.
 */
export const deletePlacement = createAsyncThunk('placementDetails/removePlacement', async (accommodationId: number) => {
  const { data } = await JmrService.deleteAccommodation(accommodationId);
  return data;
});

/**
 * Get accommodation statuses.
 */
export const getAccommodationStatus = createAsyncThunk(
  'placementDetails/getAccommodationStatus',
  async (siteId: number) => {
    const { data } = await PlacementDetailsService.getAccommodationStatus(siteId);
    return data;
  },
);

/**
 * Get accommodation statuses for accommodation given an accommodation id.
 */
export const getAccommodationStatusForAccommodation = createAsyncThunk(
  'placementDetails/getAccommodationStatusForAccommodation',
  async (accommodationId: number) => {
    const { data } = await PlacementDetailsService.getAccommodationStatusForAccommodation(accommodationId);
    return data;
  },
);

/**
 * Update accommodation with associate information change.
 */
export const updateAssociateInformation = createAsyncThunk(
  'placementDetails/updateAssociateInformation',
  async (_, { getState }) => {
    const state: any = getState();
    const accommodationId = state.placementDetailsPage.placementId;
    const editAssociateInfoFormData = state.placementDetailsPage.editAssociateInfoModalFormData;
    const editAssociateInfoFormDataPayload = {
      accommodationId: accommodationId,
      login: editAssociateInfoFormData.login,
      shiftCode: editAssociateInfoFormData.homeShift,
      externalCaseId: editAssociateInfoFormData.caseId,
      caseSource: editAssociateInfoFormData.caseSource,
      job: editAssociateInfoFormData.job,
      employeeId: editAssociateInfoFormData.employeeId,
      personId: editAssociateInfoFormData.personId,
      fullName: editAssociateInfoFormData.fullName,
    };
    const responseData = await JmrService.updateAssociateInfo(editAssociateInfoFormDataPayload);
    return responseData;
  },
);

/**
 * Map the enums to the value the backend expects.
 */
export const getReferralClaimsStatus = (status: ReferralAndClaimsStatusEnum) => {
  switch (status) {
    case ReferralAndClaimsStatusEnum.HasReachedLimit:
      return 'REACH_LIMIT';
    case ReferralAndClaimsStatusEnum.HasPermanentRestrictions:
      return 'REQUIRE_DLS';
    case ReferralAndClaimsStatusEnum.HasHadWorkRelatedClaimDenied:
      return 'DENY_CLAIM';
    default:
      return '';
  }
};

/**
 * Map the enums to the value the backend expects.
 */
const getAgreeWithHealthCareRecommendations = (status: AgreeWithHealthCareRecommendationsEnum) => {
  switch (status) {
    case AgreeWithHealthCareRecommendationsEnum.IsUncertain:
      return 'UNCERTAIN';
    case AgreeWithHealthCareRecommendationsEnum.DoesNotAgree:
      return 'NOT_AGREE';
    default:
      return '';
  }
};

/**
 * Update accommodation email.
 */
export const updateAccommodationEmail = createAsyncThunk(
  'placementDetails/updateAccommodationEmail',
  async (_, { getState }) => {
    const { placementId, sendEmailFormData } = await (getState() as any).placementDetailsPage;
    const payload = {
      accommodationId: +placementId,
      emailType: sendEmailFormData.emailType,
      emailRecipient: sendEmailFormData.emailRecipients,
      placementDetails: sendEmailFormData.placementDetails || sendEmailFormData.actionsForHr,
      dlsStatus: getReferralClaimsStatus(sendEmailFormData.referralAndClaimsStatus),
      decision: getAgreeWithHealthCareRecommendations(sendEmailFormData.agreeWithHealthCareRecommendations),
      actionAreaManager: sendEmailFormData.areaManagerActionsNeeded,
      firstDayReturn: sendEmailFormData.firstDayReturn,
      lastDayWorked: sendEmailFormData.lastDayWorked,
      firstDayAbsent: sendEmailFormData.firstDayAbsent,
      permanentRestrictionDate: sendEmailFormData.dateOfPermanentRestrictions,
    };
    const { data } = await PlacementDetailsService.updateEmailStatus(payload);
    return data;
  },
);

/**
 * Initiate placement transfer.
 */
export const initiatePlacementTransfer = createAsyncThunk(
  'placementDetails/initiatePlacementTransfer',
  async (_, { getState }) => {
    const state = await (getState() as any);
    const { placementId, placementDetails, confirmTransferPayload } = await (getState() as any).placementDetailsPage;
    const { allowedSites } = state.sites;
    const sourceSiteId = allowedSites.filter((site: Site) => site.site === placementDetails.fc)[0].id;
    const payload: InitiatePlacementTransfer = {
      accommodationId: placementId,
      emailRecipients: confirmTransferPayload.emailRecipients,
      originSiteId: sourceSiteId,
      destinationSiteId: confirmTransferPayload.destinationSiteId,
    };
    const { status } = await PlacementDetailsService.initiatePlacementTransfer(payload);
    return { status, fc: placementDetails.fc };
  },
);

/**
 * Get email settings by site
 */
export const getEmailSettingsForSite = createAsyncThunk(
  'placementDetails/getEmailSettings',
  async (_, { getState }) => {
    const state = await (getState() as any);
    const { placementDetails } = state.placementDetailsPage;
    const { allowedSites } = state.sites;
    const { data } = await EmailDistributionService.getForSite(
      allowedSites.filter((site: Site) => site.site === placementDetails.fc)[0].id,
    );
    return data;
  },
);

/**
 * Update placement end date
 */
export const updatePlacementEndDate = createAsyncThunk(
  'placementDetails/updateAccommodationEndDate',
  async (newDate: string, { getState }) => {
    const { placementId } = await (getState() as any).placementDetailsPage;
    const timeStamp = datePickerStringToDateObj(newDate)?.getTime();
    if (timeStamp) {
      const payload = {
        accommodationId: +placementId,
        endDate: timeStamp,
      };
      const { data } = await PlacementDetailsService.updatePlacementEndDate(payload);
      return data;
    }
  },
);

/**
 * Get s3 keys for accommodation
 */
export const getAccommodationKeys = createAsyncThunk(
  'placementDetails/getAccommodationKeys',
  async (accommodationId: number) => {
    const { data } = await PlacementDetailsService.getAccommodationKeys(accommodationId);
    return data;
  },
);

/**
 * Get employee info from people API.
 */
export const getEmployeeInfo = createAsyncThunk('placementDetails/getEmployeeInfo', async (employeeLogin: string) => {
  const { data } = await PeopleApiService.getEmployeeInfo(employeeLogin);
  return data;
});

/**
 * Fetch a list of Austin cases (incidents).
 */
export const fetchAustinCases = createAsyncThunk(
  'newJmr/fetchAustinCases',
  async (payload: { login: string; placementId: number }) => {
    const [fetchForAssociateResult, fetchForAccommodationResult] = await Promise.allSettled([
      IncidentService.fetchForAssociate(payload.login),
      IncidentService.fetchForAccommodation(payload.placementId),
    ]);
    const associateIncidents: AustinCase[] =
      fetchForAssociateResult.status === 'fulfilled' ? fetchForAssociateResult.value.data : [];
    const accommodationIncident: AustinCase | null =
      fetchForAccommodationResult.status === 'fulfilled' ? fetchForAccommodationResult.value?.data : null;
    let allIncidents = [...associateIncidents];
    // If we have an accommodation incident, check for duplicates using caseId
    if (!!accommodationIncident) {
      const isDuplicate = associateIncidents.some(
        (incident: AustinCase) => incident.caseId === accommodationIncident!.caseId,
      );
      if (!isDuplicate) {
        // Add accommodation incident to the front of the list if it's unique
        allIncidents = [accommodationIncident, ...associateIncidents];
      }
    }
    return allIncidents;
  },
);

export const getUpdateRestrictionAllowed = createAsyncThunk(
  'placementDetails/getUpdateRestrictionAllowed',
  async (accommodationId: number) => {
    const { data } = await JmrService.getUpdateRestrictionAllowed(accommodationId);
    return data;
  },
);

export const getBccEmailList = createAsyncThunk(
  'placementDetails/getBccEmailList',
  async (payload: { placementId: number; emailType: string }) => {
    const { data } = await JmrService.getBccEmailById(payload.placementId, payload.emailType);
    return data;
  },
);

export const getPlacementSite = createAsyncThunk('placementDetails/getPlacementSite', async (siteId: number) => {
  const { data } = await SitesService.getSiteById(siteId);
  return data;
});

// **** Setup **** //

const initialState: PlacementDetailsPageState = {
  placementId: 0,
  selectedTab: 'placement-selections',
  placementDetails: {} as PlacementDetails,
  isFetchingPlacementDetails: false,
  placementSite: undefined,
  accommodationStatus: [],
  isUpdateStatusModalOpen: false,
  isEmailModalOpen: false,
  isOomdModalOpen: false,
  sendEmailFormData: {} as SendEmailFormData,
  isUpdateRestrictionDatesModalOpen: false,
  newEndDateForm: { endDate: '' },
  sendEmailFormErrors: {},
  isConfirmDeleteModalOpen: false,
  jmrS3Keys: {},
  isConfirmUpdateRestrictionsModalOpen: false,
  isAlternativePlacementModalOpen: false,
  isInitiateTransferModalOpen: false,
  isRestrictionsModalOpen: false,
  isEditAssociateInfoModalOpen: false,
  isConfirmTransferModalOpen: false,
  confirmTransferPayload: {} as InitiatePlacementTransferPayloadProps,
  editAssociateInfoModalFormData: {} as EditAssociateInfoModalFormData,
  oomdFormData: {} as OomdForm,
  isEditAssociateInfoSaving: false,
  redirectUrl: '',
  austinCases: [],
  isUploadRfiModalOpen: false,
  bccEmailList: [],
  transferModalEmails: '',
  isSiteParticipatingInDecisionHubPilot: undefined,
  isRestrictionEntriesErrorModalOpen: false,
};

const { reducer, actions } = createSlice({
  name: 'placementDetailsPageSlice',
  initialState,
  reducers: {
    setSelectedTab: (state, action: PayloadAction<string>) => {
      state.selectedTab = action.payload;
    },
    setPlacementDetails: (state, action: PayloadAction<PlacementDetails>) => {
      state.placementDetails = action.payload;
    },
    setIsUpdateStatusModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isUpdateStatusModalOpen = action.payload;
    },
    setIsEmailModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isEmailModalOpen = action.payload;
    },
    setIsOomdModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isOomdModalOpen = action.payload;
    },
    setUploadRfiModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isUploadRfiModalOpen = action.payload;
    },
    setPlacementId: (state, action: PayloadAction<number>) => {
      state.placementId = action.payload;
    },
    setSendEmailFormData: (state, action: PayloadAction<SendEmailFormData>) => {
      state.sendEmailFormData = action.payload;
    },
    setSendEmailFormErrors: (state, action: PayloadAction<ErrorRecord<SendEmailFormData>>) => {
      state.sendEmailFormErrors = action.payload;
    },
    setIsConfirmDeleteModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isConfirmDeleteModalOpen = action.payload;
    },
    setIsUpdateRestrictionDatesModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isUpdateRestrictionDatesModalOpen = action.payload;
    },
    setNewEndDateForm: (state, action: PayloadAction<UpdateEndDateForm>) => {
      state.newEndDateForm = action.payload;
    },
    setJmrS3Keys: (state, action: PayloadAction<object>) => {
      state.jmrS3Keys = action.payload;
    },
    setIsConfirmUpdateRestrictionsModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isConfirmUpdateRestrictionsModalOpen = action.payload;
    },
    setIsAlternativePlacementModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isAlternativePlacementModalOpen = action.payload;
    },
    setIsInitiateTransferModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isInitiateTransferModalOpen = action.payload;
    },
    setIsRestrictionsModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isRestrictionsModalOpen = action.payload;
    },
    setIsConfirmTransferModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isConfirmTransferModalOpen = action.payload;
    },
    setConfirmTransferPayload: (state, action: PayloadAction<InitiatePlacementTransferPayloadProps>) => {
      state.confirmTransferPayload = action.payload;
    },
    setOomdFormData: (state, action: PayloadAction<OomdForm>) => {
      state.oomdFormData = action.payload;
    },
    setIsEditAssociateInfoModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isEditAssociateInfoModalOpen = action.payload;
    },
    setAssociateInfoFormData: (state, action) => {
      state.editAssociateInfoModalFormData = action.payload;
    },
    setIsEditAssociateInfoSaving: (state, action) => {
      state.isEditAssociateInfoSaving = action.payload;
    },
    setRedirectUrl: (state, action: PayloadAction<string>) => {
      state.redirectUrl = action.payload;
    },
    setIsRestrictionEntriesErrorModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isRestrictionEntriesErrorModalOpen = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getPlacementDetails.pending, (state) => {
      state.isFetchingPlacementDetails = true;
    });
    builder.addCase(getPlacementDetails.fulfilled, (state, { payload }) => {
      state.isFetchingPlacementDetails = false;
      state.placementDetails = formatPlacementDetailsPayload(payload);
    });
    builder.addCase(getPlacementDetails.rejected, (state) => {
      state.isFetchingPlacementDetails = false;
    });
    builder.addCase(getAccommodationStatus.fulfilled, (state, { payload }) => {
      state.accommodationStatus = [...payload]?.sort(
        (a: AccommodationStatusWithDecisionByLoginNeeded, b: AccommodationStatusWithDecisionByLoginNeeded) => {
          return a.status.localeCompare(b.status);
        },
      );
    });
    builder.addCase(getAccommodationStatusForAccommodation.fulfilled, (state, { payload }) => {
      state.accommodationStatus = [...payload]?.sort((a: AccommodationStatus, b: AccommodationStatus) => {
        return a.status.localeCompare(b.status);
      });
    });
    builder.addCase(downloadOOMD.fulfilled, (state, { payload }) => {
      if (payload) {
        downloadFile(createFileUrl(payload, 'application/pdf'), 'oomd-form.pdf');
        state.isOomdModalOpen = false;
      }
    });
    builder.addCase(updateAccommodationEmail.fulfilled, (state) => {
      state.isEmailModalOpen = false;
    });
    builder.addCase(getEmailSettingsForSite.fulfilled, (state, { payload }) => {
      state.emailSettings = payload;
    });
    builder.addCase(updatePlacementEndDate.fulfilled, (state) => {
      state.isUpdateRestrictionDatesModalOpen = false;
    });
    builder.addCase(getAccommodationKeys.fulfilled, (state, { payload }) => {
      state.jmrS3Keys = createHashMapFromS3Keys(payload);
    });
    builder.addCase(getBccEmailList.fulfilled, (state, { payload }) => {
      state.bccEmailList = payload;
    });
    builder.addCase(getEmployeeInfo.fulfilled, (state, { payload }) => {
      state.editAssociateInfoModalFormData.employeeId = payload.basicInfo?.legacyEmployeeId;
      state.editAssociateInfoModalFormData.job = payload.basicInfo?.businessTitle;
      state.editAssociateInfoModalFormData.homeShift = payload.job?.workScheduleId;
      state.editAssociateInfoModalFormData.fullName = `${payload.basicInfo?.firstName} ${payload.basicInfo?.lastName}`;
    });
    builder.addCase(getPlacementIdByExternalIdAndRedirect.fulfilled, (state, { payload }) => {
      if (isNumber(payload)) {
        state.redirectUrl = `/placementDetails/${payload}`;
      } else {
        state.redirectUrl = `/newJMR`;
      }
    });
    builder.addCase(fetchAustinCases.fulfilled, (state, { payload }) => {
      state.austinCases = payload;
    });
    builder.addCase(getEmailsForTransferPlacement.fulfilled, (state, { payload }) => {
      state.transferModalEmails = payload;
    });
    builder.addCase(initiatePlacementTransfer.fulfilled, (state, { payload }) => {
      if (payload.status === 200) {
        state.redirectUrl = `/placementsOverview/${payload.fc}/pendingPlacements`;
      }
    });
    builder.addCase(getPlacementSite.fulfilled, (state, { payload }) => {
      state.placementSite = payload;
      state.isSiteParticipatingInDecisionHubPilot = payload.participatingInDecisionHubPilot;
    });
  },
});

// **** Export default **** //

export const {
  setSelectedTab,
  setPlacementDetails,
  setIsUpdateStatusModalOpen,
  setPlacementId,
  setIsEmailModalOpen,
  setSendEmailFormErrors,
  setSendEmailFormData,
  setIsConfirmDeleteModalOpen,
  setIsUpdateRestrictionDatesModalOpen,
  setNewEndDateForm,
  setIsConfirmUpdateRestrictionsModalOpen,
  setIsAlternativePlacementModalOpen,
  setIsInitiateTransferModalOpen,
  setIsRestrictionsModalOpen,
  setIsConfirmTransferModalOpen,
  setConfirmTransferPayload,
  setIsOomdModalOpen,
  setUploadRfiModalOpen,
  setOomdFormData,
  setIsEditAssociateInfoModalOpen,
  setAssociateInfoFormData,
  setIsEditAssociateInfoSaving,
  setRedirectUrl,
  setIsRestrictionEntriesErrorModalOpen,
} = actions;

export default reducer;
